import { forwardRef } from "react"
import { motion } from "framer-motion"

const Button = forwardRef(
  (
    {
      children,
      variant = "contained",
      color = "primary",
      size = "medium",
      disabled = false,
      fullWidth = false,
      startIcon,
      endIcon,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    // Determine base styles based on variant
    const getBaseStyles = () => {
      switch (variant) {
        case "contained":
          return "shadow-md hover:shadow-lg active:shadow-sm transition-shadow"
        case "outlined":
          return "border-2 bg-transparent"
        case "text":
          return "bg-transparent"
        default:
          return "shadow-md hover:shadow-lg active:shadow-sm transition-shadow"
      }
    }

    // Determine color styles based on color and variant
    const getColorStyles = () => {
      if (disabled) {
        return "bg-gray-300 text-gray-500 cursor-not-allowed"
      }

      switch (variant) {
        case "contained":
          switch (color) {
            case "primary":
              return "bg-[#FD7211] hover:bg-[#e56208] text-white"
            case "secondary":
              return "bg-purple-600 hover:bg-purple-700 text-white"
            case "error":
              return "bg-red-600 hover:bg-red-700 text-white"
            case "success":
              return "bg-green-600 hover:bg-green-700 text-white"
            case "warning":
              return "bg-yellow-600 hover:bg-yellow-700 text-white"
            case "info":
              return "bg-cyan-600 hover:bg-cyan-700 text-white"
            default:
              return "bg-blue-600 hover:bg-blue-700 text-white"
          }
        case "outlined":
          switch (color) {
            case "primary":
              return "border-blue-600 text-blue-600 hover:bg-blue-50"
            case "secondary":
              return "border-purple-600 text-purple-600 hover:bg-purple-50"
            case "error":
              return "border-red-600 text-red-600 hover:bg-red-50"
            case "success":
              return "border-green-600 text-green-600 hover:bg-green-50"
            case "warning":
              return "border-yellow-600 text-yellow-600 hover:bg-yellow-50"
            case "info":
              return "border-cyan-600 text-cyan-600 hover:bg-cyan-50"
            default:
              return "border-blue-600 text-blue-600 hover:bg-blue-50"
          }
        case "text":
          switch (color) {
            case "primary":
              return "text-blue-600 hover:bg-blue-50"
            case "secondary":
              return "text-purple-600 hover:bg-purple-50"
            case "error":
              return "text-red-600 hover:bg-red-50"
            case "success":
              return "text-green-600 hover:bg-green-50"
            case "warning":
              return "text-yellow-600 hover:bg-yellow-50"
            case "info":
              return "text-cyan-600 hover:bg-cyan-50"
            default:
              return "text-blue-600 hover:bg-blue-50"
          }
        default:
          return "bg-blue-600 hover:bg-blue-700 text-white"
      }
    }

    // Determine size styles
    const getSizeStyles = () => {
      switch (size) {
        case "small":
          return "text-xs px-2 py-1 rounded"
        case "medium":
          return "text-sm px-4 py-2 rounded-md"
        case "large":
          return "text-base px-6 py-3 rounded-lg w-full"
        default:
          return "text-sm px-4 py-2 rounded-md"
      }
    }

    return (
      <motion.button
        ref={ref}
        className={`font-popinsMedium inline-flex items-center justify-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${getBaseStyles()} ${getColorStyles()} ${getSizeStyles()} ${
          fullWidth ? "w-full" : ""
        } ${className || ""}`}
        whileHover={!disabled ? { scale: 1.02 } : {}}
        whileTap={!disabled ? { scale: 0.98 } : {}}
        onClick={!disabled ? onClick : undefined}
        disabled={disabled}
        {...props}
      >
        {startIcon && <span className="mr-2">{startIcon}</span>}
        {children}
        {endIcon && <span className="ml-2">{endIcon}</span>}
      </motion.button>
    )
  },
)

Button.displayName = "Button"

export default Button
